import React, { useEffect, useState, useCallback } from 'react';
import { MessageCircleHeart, User, RefreshCw, Search, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { getGuestWishesApi, deleteGuestWishApi } from '../../api/cardApi';
import '../../CSS/GuestWishComponent.css';

const PAGE_SIZE = 10;

const formatWishDate = (createdAt) => {
  if (!createdAt) return '';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '';
  return d
    .toLocaleDateString('en-MY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .replace(/([ap]\.?m\.?)/i, (m) => m.replace(/\./g, '').toUpperCase());
};

const ICON_COLOUR = '#888';
const ICON_BG = 'rgba(0, 0, 0, 0.06)';

const WishCard = ({ wish, onDeleteClick }) => {
  return (
    <article className="gwc-wish-card">
      <div className="gwc-wish-avatar" style={{ background: ICON_BG }}>
        <User size={22} color={ICON_COLOUR} />
      </div>
      <div className="gwc-wish-body">
        <div className="gwc-wish-header">
          <span className="gwc-wish-name">{wish.senderName || 'Tetamu'}</span>
          {wish.createdAt && (
            <span className="gwc-wish-date">{formatWishDate(wish.createdAt)}</span>
          )}
        </div>
        <p className="gwc-wish-message">{wish.message || '-'}</p>
      </div>

      <div className="gwc-wish-actions">
        <IconButton
          size="small"
          onClick={() => onDeleteClick(wish.id)}
          sx={{
            color: '#c62828',
            opacity: 0.7,
            '&:hover': { opacity: 1, backgroundColor: 'rgba(198,40,40,0.08)' },
            p: 0.5,
          }}
          title="Padam ucapan"
          aria-label="Delete wish"
        >
          <Trash2 size={14} />
        </IconButton>
      </div>
    </article>
  );
};

const SkeletonCard = () => (
  <div className="gwc-skeleton-card">
    <div className="gwc-skeleton-avatar" />
    <div className="gwc-skeleton-lines">
      <div className="gwc-skeleton-line gwc-skeleton-name" />
      <div className="gwc-skeleton-line gwc-skeleton-msg" />
    </div>
  </div>
);

const GuestWishComponent = ({ uniqueCode }) => {
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchWishes = useCallback(async (isRefresh = false) => {
    if (!uniqueCode) return;
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      const data = await getGuestWishesApi(uniqueCode);
      setWishes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch wishes:', err);
      setError('Gagal memuatkan ucapan tetamu. Sila cuba lagi.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [uniqueCode]);

  useEffect(() => {
    fetchWishes();
  }, [fetchWishes]);

  // Reset to page 1 whenever search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    setDeleteLoading(true);
    try {
      await deleteGuestWishApi(uniqueCode, deleteTargetId);
      setWishes((prev) => {
        const updated = prev.filter((w) => w.id !== deleteTargetId);
        // If current page becomes empty after delete, go back one page
        const newTotalPages = Math.ceil(
          updated.filter((w) => {
            const q = searchQuery.toLowerCase();
            return (
              (w.senderName || '').toLowerCase().includes(q) ||
              (w.message || '').toLowerCase().includes(q)
            );
          }).length / PAGE_SIZE
        );
        if (currentPage > newTotalPages && newTotalPages > 0) {
          setCurrentPage(newTotalPages);
        }
        return updated;
      });
    } catch (err) {
      console.error('Failed to delete wish:', err?.response?.status, err?.response?.data);
    } finally {
      setDeleteLoading(false);
      setDeleteTargetId(null);
    }
  };

  const filteredWishes = wishes.filter((w) => {
    const q = searchQuery.toLowerCase();
    return (
      (w.senderName || '').toLowerCase().includes(q) ||
      (w.message || '').toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredWishes.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedWishes = filteredWishes.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="gwc-container">
      {/* Header */}
      <div className="gwc-header">
        <div className="gwc-header-title-row">
          <div className="gwc-header-icon">
            <MessageCircleHeart size={20} />
          </div>
          <div>
            <h2 className="gwc-title">Ucapan Tetamu</h2>
            <p className="gwc-subtitle">Guest Wishes</p>
          </div>
        </div>

        <div className="gwc-header-right">
          <div className="gwc-badge">
            {loading ? '...' : `${wishes.length} ucapan`}
          </div>
          <button
            id="gwc-refresh-btn"
            className={`gwc-refresh-btn ${refreshing ? 'gwc-spinning' : ''}`}
            onClick={() => fetchWishes(true)}
            disabled={refreshing || loading}
            title="Refresh"
            aria-label="Refresh wishes"
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {/* Search */}
      {!loading && wishes.length > 0 && (
        <div className="gwc-search-wrapper">
          <Search size={14} className="gwc-search-icon" />
          <input
            id="gwc-search-input"
            className="gwc-search-input"
            type="text"
            placeholder="Cari nama atau ucapan..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}

      {/* Content */}
      <div className="gwc-list">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
        ) : error ? (
          <div className="gwc-error-state">
            <p className="gwc-error-text">{error}</p>
            <button className="gwc-retry-btn" onClick={() => fetchWishes()}>
              Cuba Lagi
            </button>
          </div>
        ) : filteredWishes.length === 0 ? (
          <div className="gwc-empty-state">
            <div className="gwc-empty-icon">
              <MessageCircleHeart size={40} />
            </div>
            <p className="gwc-empty-title">
              {searchQuery ? 'Tiada hasil carian' : 'Belum ada ucapan'}
            </p>
            <p className="gwc-empty-sub">
              {searchQuery
                ? 'Cuba kata kunci lain.'
                : 'Ucapan dari tetamu akan muncul di sini apabila mereka menghantar.'}
            </p>
          </div>
        ) : (
          pagedWishes.map((wish, index) => (
            <WishCard
              key={wish.id ?? index}
              wish={wish}
              onDeleteClick={setDeleteTargetId}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && filteredWishes.length > PAGE_SIZE && (
        <div className="gwc-pagination">
          <button
            className="gwc-page-btn"
            onClick={handlePrev}
            disabled={safePage === 1}
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </button>

          <span className="gwc-page-info">
            <span className="gwc-page-current">{safePage}</span>
            <span className="gwc-page-sep"> / </span>
            <span className="gwc-page-total">{totalPages}</span>
          </span>

          <button
            className="gwc-page-btn"
            onClick={handleNext}
            disabled={safePage === totalPages}
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTargetId}
        onClose={() => !deleteLoading && setDeleteTargetId(null)}
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Padam Ucapan</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            Adakah anda pasti ingin memadam ucapan ini? Ucapan yang telah dipadam tidak boleh dikembalikan.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteTargetId(null)}
            disabled={deleteLoading}
            sx={{ textTransform: 'none', color: '#4F4F4F', fontSize: 13, backgroundColor: '#e7e7e7ff', borderRadius: '50px' }}
          >
            Batal
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
            sx={{
              textTransform: 'none',
              fontSize: 13,
              color: '#c62828',
              backgroundColor: '#fbbcbca8',
              borderRadius: '50px',
              minWidth: 90,
            }}
          >
            {deleteLoading ? <CircularProgress size={16} sx={{ color: '#c62828' }} /> : 'Padam'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GuestWishComponent;
